/**
 * configurable-engine.ts
 * Task 5b.22 — Shared Zod contracts for the LOCX configurable engine endpoints.
 *
 * Covers:
 *   - FieldDefinitionResponse — single resolved field definition (for a service type + company)
 *   - ResolvedFieldsResponse  — envelope wrapping the dynamic fields array
 *   - AssetTypeResponse       — an asset type row (LOCX-owned)
 *   - AssetCreate             — request body to create an asset
 *   - AssetResponse           — response shape for a persisted asset
 *   - ServiceOrderAssetResponse — response shape for a service_order_asset link
 *
 * ZERO Hono/Drizzle/infrastructure imports — SL-6.3 / depcruise contracts-self-contained.
 * These types are consumed by both the Hono backend (http layer) and the Vue frontend.
 *
 * Spec: SL-6.1 / SL-6.3 / design §5d / tasks 5b.22
 */
import { z } from 'zod';

// ---------------------------------------------------------------------------
// Closed data_type vocabulary (red line #1 from LOCX CLAUDE.md)
// Must stay in sync with the data_type pgEnum in servicios.schema.ts.
// ---------------------------------------------------------------------------

export const DataTypeSchema = z.enum([
  'text',
  'number',
  'integer',
  'boolean',
  'date',
  'datetime',
  'money',
  'select',
]);

export type DataType = z.infer<typeof DataTypeSchema>;

// ---------------------------------------------------------------------------
// FieldDefinitionResponse — one resolved field definition
// ---------------------------------------------------------------------------

/**
 * A single resolved field definition for a (company, service_type).
 * All company_field_override values have already been merged by FieldResolver.
 * This is the shape returned by GET /servicios/service-types/:id/fields.
 *
 * Mirrors ResolvedFieldDefinition in FieldResolver.ts — but as a serializable
 * Zod schema for API contract (no readonly modifiers, array instead of ReadonlyArray).
 */
export const FieldDefinitionResponseSchema = z.object({
  id: z.string().uuid(),
  /** null = base (system-wide) field; set = company-specific addition. */
  companyId: z.string().uuid().nullable(),
  serviceTypeId: z.string().uuid(),
  key: z.string(),
  label: z.string(),
  dataType: DataTypeSchema,
  required: z.boolean(),
  defaultValue: z.string().nullable(),
  position: z.number().int(),
  isGridColumn: z.boolean(),
  gridPosition: z.number().int().nullable(),
  /** Bounded validation params — NOT formulas or expressions (red line #2). */
  minValue: z.string().nullable(),
  maxValue: z.string().nullable(),
  maxLength: z.number().int().nullable(),
  pattern: z.string().nullable(),
  active: z.boolean(),
  /**
   * Allowed options for data_type='select' fields.
   * Empty array for all other data types.
   */
  options: z.array(z.object({ value: z.string(), label: z.string() })),
});

export type FieldDefinitionResponse = z.infer<typeof FieldDefinitionResponseSchema>;

// ---------------------------------------------------------------------------
// ResolvedFieldsResponse — envelope for the resolved fields endpoint
// ---------------------------------------------------------------------------

/**
 * Response shape for GET /servicios/service-types/:id/fields.
 * Returns the full resolved field set for a (company, service_type) pair —
 * to be used by the frontend before order creation to build dynamic forms.
 *
 * Spec: design §5d.1
 */
export const ResolvedFieldsResponseSchema = z.object({
  serviceTypeId: z.string().uuid(),
  fields: z.array(FieldDefinitionResponseSchema),
});

export type ResolvedFieldsResponse = z.infer<typeof ResolvedFieldsResponseSchema>;

// ---------------------------------------------------------------------------
// AssetTypeResponse — an asset type (LOCX-owned, servicios schema)
// ---------------------------------------------------------------------------

/**
 * Response shape for an asset type row.
 * Asset types are LOCX-owned (servicios.asset_type — Layer 3).
 *
 * Spec: design §5d Layer 3
 */
export const AssetTypeResponseSchema = z.object({
  id: z.string().uuid(),
  code: z.string(),
  label: z.string(),
  active: z.boolean(),
  /**
   * locx-paridad-operativa-bramix Slice A (MR-1/AT-8) — whether this asset
   * type exposes an hour-meter / odometer. Exposed here (not only on the
   * admin response) so the operator picker can drive MR-6's conditional
   * obligatoriedad without a second request or reimplementing the rule.
   */
  tracksHours: z.boolean(),
  tracksKm: z.boolean(),
});

export type AssetTypeResponse = z.infer<typeof AssetTypeResponseSchema>;

// ---------------------------------------------------------------------------
// AssetCreate — request body to create an asset
// ---------------------------------------------------------------------------

// Closed vocabulary — Vaca Muerta / Neuquén basin operational hubs (25 jul
// 2026 — migrated OFF the asset_attribute_def EAV system into a fixed column,
// same posture as `status` above: a closed, code-controlled list, not
// per-tenant configurable. Single source of truth for both backend
// validation and the frontend picker (labels/coordinates stay in
// apps/frontend/.../dashboard/localities.ts, a presentation-only concern).
export const LOCALITY_VALUES = [
  'anelo',
  'neuquen_capital',
  'rincon_de_los_sauces',
  'plaza_huincul',
  'cutral_co',
  'centenario',
  'san_patricio_del_chanar',
  'sauzal_bonito',
] as const;

/**
 * Request body for POST /servicios/assets.
 * tenantId is NEVER here — it comes from TenantContext (KC-3.5).
 *
 * Spec: design §5d Layer 3 / KC-3.5
 */
export const AssetCreateSchema = z.object({
  assetTypeId: z.string().uuid('assetTypeId must be a valid UUID'),
  serialNumber: z.string().min(1, 'serialNumber is required').max(256),
  description: z.string().max(1024).optional(),
  /** Initial lifecycle status — defaults to 'active' if not supplied. */
  status: z.enum(['active', 'inactive', 'maintenance']).optional().default('active'),
  // Denormalized text reference to compras.item_master.code — no FK, no
  // validated relation yet (BM Inspecciones import, 2026-08-19). See the
  // itemMasterCode comment on servicios.schema.ts's `asset` table.
  itemMasterCode: z.string().max(64).optional(),
  // Who legally owns this asset (merge-holding-group-tenant design.md §1/§2) —
  // bare ref to identity.organization, no FK (cross-schema). Omit for
  // single-firma tenants, where there's nothing to pick. 22 jul 2026: was a
  // column with no way to ever set it — this closes that gap.
  legalEntityId: z.string().uuid().optional(),
  // Centro de costo al que pertenece el activo (25 jul 2026) — bare ref to
  // catalog.cost_center, no FK (cross-schema, same convention as
  // service_order.costCenterId/fleet_failure_report.costCenterId). Confirmed
  // contra el legacy real (bramix `vehiculo.centro_costo_id`): es un campo
  // estable y directo del vehículo, no algo derivado de sus órdenes de
  // servicio. Omit para tenants sin CC configurados.
  costCenterId: z.string().uuid().optional(),
  // Localidad operativa (25 jul 2026) — migrada del sistema EAV a columna fija
  // (ver LOCALITY_VALUES arriba). Omit para no asignar todavía.
  locality: z.enum(LOCALITY_VALUES).optional(),
  // Proveedor del que se alquila este activo cuando su atributo `ownership`
  // (EAV, closed vocabulary 'propio'|'alquilado') es 'alquilado' — bare ref a
  // sumi.supplier, no FK (cross-schema), validate-on-write via ISupplierLookup
  // (migration 0148, mismo posture que service_order_asset.supplierId /
  // migration 0144). Omit cuando el activo es propio o no se conoce el proveedor.
  supplierId: z.string().uuid().optional(),
  /**
   * Bag of asset_attribute_def values keyed by `key` (Marca/Modelo/N° chasis/
   * etc, whatever is configured for this assetTypeId) — 23 jul 2026, "alta de
   * vehículo" gap: asset_attribute_value had a full read path but no write
   * path at all. Validated server-side against the resolved defs for
   * assetTypeId via AssetAttributeZodBuilder (same posture as
   * CreateServiceOrderUseCase's `fields` bag / DynamicZodBuilder). Omit
   * entirely to skip attribute writes (e.g. an asset type with no defs).
   * `ownership` ('propio'|'alquilado') is written through THIS bag, same as
   * any other select attribute — see supplierId above for the one field that
   * needed a real column instead.
   */
  attributeValues: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])).optional(),
});

export type AssetCreate = z.infer<typeof AssetCreateSchema>;

// ---------------------------------------------------------------------------
// AssetUpdate — request body for PATCH /servicios/assets/:id
// ---------------------------------------------------------------------------

/**
 * Request body for PATCH /servicios/assets/:id (23 jul 2026 — no edit flow
 * existed before this at all, active OR inactive; user report).
 *
 * assetTypeId is IMMUTABLE after creation, same invariant as VehicleDocument's
 * documentTypeId and AssetType's own code — asset_attribute_def rows resolve
 * off the asset's type, so changing it would orphan the resolved attribute
 * values. attributeValues (23 jul 2026) IS editable — see AssetCreateSchema's
 * field comment for the validation posture, identical here.
 *
 * .strict() rejects assetTypeId/companyId/id — same immutability enforcement
 * pattern as AssetTypeUpdateSchema/VehicleDocumentUpdateSchema.
 */
export const AssetUpdateSchema = z
  .object({
    serialNumber: z.string().min(1, 'serialNumber is required').max(256).optional(),
    description: z.string().max(1024).nullable().optional(),
    status: z.enum(['active', 'inactive', 'maintenance']).optional(),
    legalEntityId: z.string().uuid().nullable().optional(),
    costCenterId: z.string().uuid().nullable().optional(),
    locality: z.enum(LOCALITY_VALUES).nullable().optional(),
    supplierId: z.string().uuid().nullable().optional(),
    attributeValues: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])).optional(),
  })
  .strict();

export type AssetUpdate = z.infer<typeof AssetUpdateSchema>;

// ---------------------------------------------------------------------------
// AssetResponse — response shape for a persisted asset
// ---------------------------------------------------------------------------

/**
 * Response shape for GET /servicios/assets and POST /servicios/assets.
 * Returned after asset creation or list retrieval.
 *
 * Spec: design §5d Layer 3
 */
export const AssetResponseSchema = z.object({
  id: z.string().uuid(),
  companyId: z.string().uuid(),
  assetTypeId: z.string().uuid(),
  serialNumber: z.string(),
  description: z.string().nullable(),
  status: z.string(), // 'active' | 'inactive' | 'maintenance'
  legalEntityId: z.string().uuid().nullable(),
  costCenterId: z.string().uuid().nullable(),
  locality: z.string().nullable(),
  // Proveedor del que se alquila este activo (migration 0148) — solo tiene
  // sentido cuando ownership es 'alquilado'; null en cualquier otro caso.
  supplierId: z.string().uuid().nullable(),
  /**
   * Resolved `ownership` asset_attribute_def value (EAV, closed vocabulary),
   * same mechanism daily-report.routes.ts's SL-61.3 already uses. Null when
   * the asset's type has no `ownership` attribute configured — treated as
   * 'propio' for the availability rule below, but reported as null here
   * (never silently coerced) so the UI can tell "configured as propio" apart
   * from "not configured".
   */
  ownership: z.enum(['propio', 'alquilado']).nullable(),
  /**
   * "Disponible hoy" (approved 2026-08-22): whether this asset is free to
   * assign RIGHT NOW. ALWAYS null for a third-party asset (ownership ===
   * 'alquilado') — BM cannot see G&M's real-time inventory, so a
   * true/false claim here would be misleading; the UI must show a distinct
   * "consultar al proveedor" signal instead of ever branching on this as a
   * boolean. Computed (true/false) for an owned asset (ownership === 'propio'
   * OR no ownership attribute configured — absent defaults to "propio" for
   * this rule only): false when a service_order_asset row exists whose
   * period covers the current moment AND whose order is in a non-terminal
   * workflow state; true otherwise.
   */
  availableToday: z.boolean().nullable(),
  createdAt: z.string().datetime().or(z.date()),
  updatedAt: z.string().datetime().or(z.date()),
});

export type AssetResponse = z.infer<typeof AssetResponseSchema>;

// ---------------------------------------------------------------------------
// ServiceOrderAssetResponse — response shape for a service_order_asset link
// ---------------------------------------------------------------------------

/**
 * Response shape for POST /servicios/orders/:id/assets.
 * Returned after successfully linking an asset to a service order.
 *
 * Pricing fields (SL-37, locx-rental-bm Slice 4):
 *   unitPrice, rateType, currency — nullable; present only for rental service types.
 *   Null for non-rental assets or links created before the pricing migration.
 *
 * supplierId (fix/rental-wizard-paso2, migration 0144): nullable — the
 * per-line supplier the rental wizard's step 2 now records against THIS
 * asset link, replacing the old wizard-local, non-persisted prefill. Null
 * when no supplier was picked for the line, or for non-rental asset links.
 *
 * Spec: design §5d Layer 3 / design §5d.1 / SL-37.1 / SL-37.5
 */
export const ServiceOrderAssetResponseSchema = z.object({
  id: z.string().uuid(),
  companyId: z.string().uuid(),
  serviceOrderId: z.string().uuid(),
  assetId: z.string().uuid(),
  quantity: z.string(),
  periodFrom: z.string().datetime().or(z.date()).nullable(),
  periodTo: z.string().datetime().or(z.date()).nullable(),
  createdAt: z.string().datetime().or(z.date()),
  // Pricing fields — nullable; required only when pricing capability is ON (SL-37.3 / SL-37.4)
  unitPrice: z.string().nullable(),
  rateType: z.enum(['daily', 'weekly', 'monthly', 'flat']).nullable(),
  currency: z.string().nullable(),
  supplierId: z.string().uuid().nullable(),
});

export type ServiceOrderAssetResponse = z.infer<typeof ServiceOrderAssetResponseSchema>;

/**
 * UpdateServiceOrderAssetSchema — request body for
 * PATCH /servicios/service-order-assets/:id (item 5 point 5, "corregir un
 * dato mal cargado en un paso ya completado"). Every field is optional —
 * PATCH semantics: an omitted key leaves that column untouched; an explicit
 * `null` on a nullable field clears it. Mirrors the LinkAssetSchema fields
 * used by POST /servicios/orders/:id/assets (same formats/validation), minus
 * `assetId` — the line's underlying asset is never reassigned by an edit.
 */
export const UpdateServiceOrderAssetSchema = z
  .object({
    quantity: z.string().regex(/^\d+(\.\d+)?$/, 'quantity must be a numeric string').optional(),
    unitPrice: z
      .string()
      .regex(/^\d+(\.\d+)?$/, 'unitPrice must be a numeric string (e.g. "1500.00")')
      .nullable()
      .optional(),
    rateType: z
      .enum(['daily', 'weekly', 'monthly', 'flat'], {
        errorMap: () => ({ message: "rateType must be 'daily', 'weekly', 'monthly', 'flat', or null" }),
      })
      .nullable()
      .optional(),
    currency: z
      .string()
      .min(1, 'currency must not be empty when provided')
      .max(10, 'currency must be at most 10 characters')
      .nullable()
      .optional(),
    periodFrom: z.string().datetime().nullable().optional(),
    periodTo: z.string().datetime().nullable().optional(),
    supplierId: z.string().uuid('supplierId must be a valid UUID').nullable().optional(),
  })
  .strict();

export type UpdateServiceOrderAssetInput = z.infer<typeof UpdateServiceOrderAssetSchema>;

// ---------------------------------------------------------------------------
// ServiceOrderAssetListItemSchema / ServiceOrderAssetListResponseSchema
// ---------------------------------------------------------------------------

/**
 * Response shape for one item of GET /servicios/orders/:id/assets.
 *
 * locx-bm-rental-wizard Phase 5b: real per-asset items for the "Solicitar
 * compra al proveedor" handoff (`ReturnManagedStep.vue`) — previously a
 * single generic placeholder line because no list endpoint existed for an
 * order's assets. `asset` has no `name` column; this codebase's established
 * human-readable identity for an asset is `serialNumber` (+ optional
 * `description`) — same pair `FleetFailureReportRepository`'s
 * `findByIdWithAsset`/`listAllWithAsset` joins already expose, and the same
 * pair `AssetPicker.vue` already shows the operator. No new naming scheme.
 *
 * Spec: design §7B (item-mapping), tasks Phase 5b
 *
 * unitPrice/periodFrom/periodTo (locx-bm-rental-wizard-ficha-fix): the same
 * pricing columns already on `service_order_asset` (see
 * ServiceOrderAssetResponseSchema — populated by SupplierSourcingStep.vue's
 * link-asset submit) — added to this LIST projection so
 * RentalManagedDocsSummary.vue can render the item/pricing table on the
 * order detail's Ficha tab without a second endpoint. Nullable: pricing is
 * only required when the pricing capability is ON, and periodFrom/periodTo
 * are optional fields the wizard step omits when left blank.
 */
export const ServiceOrderAssetListItemSchema = z.object({
  /** item 5 point 5 ("editar línea"): the row's own id — target for PATCH /servicios/service-order-assets/:id. */
  id: z.string().uuid(),
  assetId: z.string().uuid(),
  serialNumber: z.string(),
  description: z.string().nullable(),
  quantity: z.string(),
  unitPrice: z.string().nullable(),
  /** item 5 point 5: exposed so RentalManagedDocsSummary.vue's "editar línea" modal can prefill Modalidad. */
  rateType: z.enum(['daily', 'weekly', 'monthly', 'flat']).nullable(),
  periodFrom: z.string().datetime().or(z.date()).nullable(),
  periodTo: z.string().datetime().or(z.date()).nullable(),
  /**
   * fix/rental-locx-bm FASE 2.5: the same `currency` column already on
   * service_order_asset (populated by SupplierSourcingStep.vue's link-asset
   * submit) — was persisted but never exposed on this list projection, so
   * RentalManagedDocsSummary.vue's <Money> amounts had no currency to show.
   * Nullable: a link row created without a currency (or before pricing was
   * required) has none.
   */
  currency: z.string().nullable(),
  /** item 5 point 5: exposed so RentalManagedDocsSummary.vue's "editar línea" modal can prefill Proveedor. */
  supplierId: z.string().uuid().nullable(),
  /**
   * locx-rental-contrato-alquiler Phase B (SL-86.2): 4 new nullable fields,
   * additive-only. Resolved LIVE from the asset's current attribute values
   * (SL-86.3) via AssetAttributeValueRepository.findContractAttributesForAssets
   * — never snapshotted onto the order line. `null` when the asset has no
   * value for that attribute (SL-86.4) — never a fabricated default.
   *
   * `.optional()` in ADDITION to `.nullable()` (not just `.nullable()`):
   * required to keep this additive-only, per SL-86 Scenario "existing
   * fields remain unchanged (backward compatibility)" — the real backend
   * always sends these as `string | null`, but pre-existing test fixtures
   * (pricing-schemas.spec.ts) constructed before this change omit the keys
   * entirely, and MUST keep parsing unchanged.
   */
  ownership: z.string().nullable().optional(),
  /** SL-85 attribute (select: nuevo/bueno/regular/a_reparar). */
  condicion: z.string().nullable().optional(),
  /** SL-85 attribute (select: si/no). */
  certificacion: z.string().nullable().optional(),
  /**
   * SL-85 attribute (money). Kept as a string on the wire (D6), same
   * posture as `unitPrice` — never a JS float (Postgres `numeric`).
   */
  valorReposicion: z.string().nullable().optional(),
});

export type ServiceOrderAssetListItem = z.infer<typeof ServiceOrderAssetListItemSchema>;

export const ServiceOrderAssetListResponseSchema = z.object({
  items: z.array(ServiceOrderAssetListItemSchema),
});

export type ServiceOrderAssetListResponse = z.infer<typeof ServiceOrderAssetListResponseSchema>;

// ---------------------------------------------------------------------------
// vehicle_document / vehicle_document_type — asset certification documents
// (HANDOFF-DOCUMENTOS.md second pass; servicios.schema.ts migration 0093).
// Mirrors rrhh's EmployeeDocument*/EmployeeDocumentType* contracts exactly —
// assetId instead of personId. fileId is a bare uuid -> identity.file,
// uploaded separately via the kernel's generic POST /files (same two-step
// flow as employee documents / person photos). Whether expiresAt is required
// depends on the referenced documentTypeId.requiresExpiration — enforced in
// the use-case, not here (Zod validates shape/format only).
// ---------------------------------------------------------------------------

export const VehicleDocumentCreateSchema = z.object({
  documentTypeId: z.string().uuid(),
  fileId: z.string().uuid().optional(),
  documentNumber: z.string().optional(),
  issuedAt: z.string().date().optional(),
  expiresAt: z.string().date().optional(),
  notes: z.string().optional(),
});
export type VehicleDocumentCreate = z.infer<typeof VehicleDocumentCreateSchema>;

export const VehicleDocumentUpdateSchema = z.object({
  fileId: z.string().uuid().nullable().optional(),
  documentNumber: z.string().nullable().optional(),
  issuedAt: z.string().date().nullable().optional(),
  expiresAt: z.string().date().nullable().optional(),
  notes: z.string().nullable().optional(),
});
export type VehicleDocumentUpdate = z.infer<typeof VehicleDocumentUpdateSchema>;

/**
 * POST /servicios/assets/:id/documents/:documentId/renew (fleet-qr-compliance
 * PR1, design.md §2.2) — inserts a NEW document row superseding the one at
 * :documentId. Same shape as Create minus documentTypeId (inherited from the
 * document being renewed, immutable across the chain).
 */
export const VehicleDocumentRenewSchema = z.object({
  fileId: z.string().uuid().optional(),
  documentNumber: z.string().optional(),
  issuedAt: z.string().date().optional(),
  expiresAt: z.string().date().optional(),
  notes: z.string().optional(),
});
export type VehicleDocumentRenew = z.infer<typeof VehicleDocumentRenewSchema>;

/**
 * `status` (design.md §2.1) is derived server-side from expiresAt — NOT
 * persisted, computed fresh on every response. `supersededAt`/`supersedesId`
 * (design.md §2.2) expose the renewal chain: supersededAt !== null means this
 * row is read-only history.
 */
export const DOCUMENT_COMPLIANCE_STATUSES = ['vigente', 'porVencer', 'vencido', 'sinVencimiento'] as const;

export const VehicleDocumentResponseSchema = z.object({
  id: z.string().uuid(),
  companyId: z.string().uuid(),
  assetId: z.string().uuid(),
  documentTypeId: z.string().uuid(),
  fileId: z.string().uuid().nullable(),
  documentNumber: z.string().nullable(),
  issuedAt: z.string().nullable(),
  expiresAt: z.string().nullable(),
  notes: z.string().nullable(),
  status: z.enum(DOCUMENT_COMPLIANCE_STATUSES),
  supersededAt: z.string().datetime().or(z.date()).nullable(),
  supersedesId: z.string().uuid().nullable(),
  createdAt: z.string().datetime().or(z.date()),
  updatedAt: z.string().datetime().or(z.date()),
});
export type VehicleDocumentResponse = z.infer<typeof VehicleDocumentResponseSchema>;

export const VehicleDocumentTypeResponseSchema = z.object({
  id: z.string().uuid(),
  companyId: z.string().uuid(),
  name: z.string(),
  requiresExpiration: z.boolean(),
  active: z.boolean(),
  createdAt: z.string().datetime().or(z.date()),
});
export type VehicleDocumentTypeResponse = z.infer<typeof VehicleDocumentTypeResponseSchema>;

// ---------------------------------------------------------------------------
// AssetDetailResponse — GET /servicios/assets/:id (asset + its documents).
// Distinct from AssetResponseSchema (the plain list/create shape) the same way
// PersonResponse vs. the person-detail spread with `documents` is handled in rrhh.
// ---------------------------------------------------------------------------

/**
 * One resolved asset_attribute_def + its typed value, as returned in the asset
 * detail response (ficha redesign, 2026-07-21). Mirrors
 * ServiceOrderFieldValueViewSchema (service-order.ts) exactly — key/label/
 * dataType come from the resolved asset_attribute_def; value is the typed
 * value or null when not set for this asset. Raw value_* EAV columns never
 * appear on the wire.
 */
export const AssetAttributeValueViewSchema = z.object({
  key: z.string(),
  label: z.string(),
  dataType: z.enum(['text', 'number', 'integer', 'boolean', 'date', 'datetime', 'money', 'select']),
  value: z.union([z.string(), z.number(), z.boolean(), z.null()]),
});
export type AssetAttributeValueView = z.infer<typeof AssetAttributeValueViewSchema>;

/**
 * One resolved asset_attribute_def, WITHOUT a value — backs
 * GET /servicios/asset-types/:id/attributes (23 jul 2026), which drives the
 * dynamic create/edit form (there is no asset id yet on create, so
 * AssetAttributeValueView's def+value shape doesn't apply). Mirrors
 * FieldDefinitionResponseSchema's shape as closely as Layer 3's simpler model
 * allows — no companyId/serviceTypeId/defaultValue/isGridColumn/gridPosition,
 * since asset attribute defs have no per-company override or grid-column
 * concept (AttributeResolver's header comment: intentionally NOT the same
 * abstraction as Layer 2's FieldResolver).
 */
export const AssetAttributeFieldResponseSchema = z.object({
  id: z.string().uuid(),
  key: z.string(),
  label: z.string(),
  dataType: z.enum(['text', 'number', 'integer', 'boolean', 'date', 'datetime', 'money', 'select']),
  required: z.boolean(),
  position: z.number().int(),
  minValue: z.string().nullable(),
  maxValue: z.string().nullable(),
  maxLength: z.number().int().nullable(),
  pattern: z.string().nullable(),
  options: z.array(z.object({ value: z.string(), label: z.string() })),
});
export type AssetAttributeFieldResponse = z.infer<typeof AssetAttributeFieldResponseSchema>;

export const ResolvedAssetAttributesResponseSchema = z.object({
  assetTypeId: z.string().uuid(),
  attributes: z.array(AssetAttributeFieldResponseSchema),
});
export type ResolvedAssetAttributesResponse = z.infer<typeof ResolvedAssetAttributesResponseSchema>;

export const AssetDetailResponseSchema = AssetResponseSchema.extend({
  documents: z.array(VehicleDocumentResponseSchema),
  /**
   * Ficha redesign (2026-07-21): resolved attribute values for this asset's
   * asset_type (Marca/Modelo/Patente/etc. for a Vehículo type, once those
   * asset_attribute_def rows are configured) — empty array when the type has
   * no attribute defs, or none were set for this asset yet.
   */
  attributeValues: z.array(AssetAttributeValueViewSchema),
});
export type AssetDetailResponse = z.infer<typeof AssetDetailResponseSchema>;

// ---------------------------------------------------------------------------
// AssetType admin contracts — add-locx-assets-feature PR1 (AT-1)
//
// IMPORTANT rules (AT-1.1 / AT-1.2):
//   - company_id / active NEVER in request bodies — come from TenantContext / DB.
//   - code is IMMUTABLE after creation — AssetTypeUpdateSchema uses .strict() to reject it.
//   - AssetTypeAdminResponseSchema is ADDITIVE over AssetTypeResponseSchema (adds companyId,
//     active, createdAt for the admin surface).
// ---------------------------------------------------------------------------

/**
 * Request body for creating a new asset type (admin only).
 * company_id is NOT here — it comes from TenantContext (KC-3.5 / AT-1.1).
 * active is NOT here — defaults to true on creation.
 *
 * Spec: AT-1.1 / design §PR1 backend
 */
export const AssetTypeCreateSchema = z
  .object({
    code: z.string().min(1, 'code is required').max(128, 'code must be at most 128 characters'),
    label: z.string().min(1, 'label is required').max(256, 'label must be at most 256 characters'),
    /**
     * locx-paridad-operativa-bramix Slice A (AT-8/MR-1) — pure data-shape
     * flags. Default false when omitted so every type that doesn't opt in
     * behaves exactly as before this change (MR-1.2).
     */
    tracksHours: z.boolean().default(false),
    tracksKm: z.boolean().default(false),
  })
  .strict(); // Rejects company_id/active/unknown fields — company_id always from TenantContext.

export type AssetTypeCreate = z.infer<typeof AssetTypeCreateSchema>;

/**
 * Request body for updating an asset type (admin only — label + meter flags).
 *
 * CODE INVARIANT: code is IMMUTABLE after creation.
 * .strict() rejects any payload that includes code or company_id (AT-1.1 / AT-1-B).
 * At least one field must be present — tracksHours/tracksKm are optional with
 * NO default here (omitted means "no change", per AT-8; only AssetTypeCreateSchema
 * defaults them to false).
 *
 * Spec: AT-1.1 / AT-8 / design §PR1 backend / design.md D6
 */
export const AssetTypeUpdateSchema = z
  .object({
    label: z.string().min(1, 'label is required').max(256, 'label must be at most 256 characters').optional(),
    tracksHours: z.boolean().optional(),
    tracksKm: z.boolean().optional(),
  })
  .strict() // Rejects code/company_id/active — load-bearing immutability enforcement.
  .refine((v) => Object.keys(v).length > 0, { message: 'at least one field must be provided' });

export type AssetTypeUpdate = z.infer<typeof AssetTypeUpdateSchema>;

/**
 * Admin response shape for a single asset type.
 * Additive over AssetTypeResponseSchema — adds companyId (nullable) and createdAt
 * for the admin surface. The ops picker uses the lighter AssetTypeResponseSchema.
 *
 * companyId: null = system-wide type; set = tenant-owned type.
 * active: false = soft-deactivated (row still exists, not returned by picker).
 *
 * camelCase keys to match every other contract response (AssetTypeResponseSchema,
 * FieldDefinitionAdminResponseSchema). Spec: AT-1.1 / AT-1-C / AT-8 / design §PR1 backend
 */
export const AssetTypeAdminResponseSchema = z.object({
  id: z.string().uuid(),
  code: z.string(),
  label: z.string(),
  /** null = system-wide type (visible to all tenants); set = tenant-owned. */
  companyId: z.string().uuid().nullable(),
  active: z.boolean(),
  createdAt: z.string().datetime(),
  tracksHours: z.boolean(),
  tracksKm: z.boolean(),
});

export type AssetTypeAdminResponse = z.infer<typeof AssetTypeAdminResponseSchema>;

/**
 * List response for GET /servicios/asset-types (admin list) — CAT-STD (2026-07-21):
 * real pagination + search + sort, replacing the previous bare-array response
 * that returned EVERY visible row unconditionally (silently fine while every
 * tenant had a handful of types, but no way to reach a "page 2" if that ever
 * changed — same latent gap usePaginatedList.ts's header comment warns about).
 *
 * Spec: AT-1.1 / design §PR1 backend
 */
export const AssetTypeAdminListResponseSchema = z.object({
  items: z.array(AssetTypeAdminResponseSchema),
  totalCount: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
  totalPages: z.number().int().nonnegative(),
});

export type AssetTypeAdminListResponse = z.infer<typeof AssetTypeAdminListResponseSchema>;

// ---------------------------------------------------------------------------
// Admin write contracts — locx-config-admin PR1a (SL-25)
//
// IMPORTANT rules (LOCX CLAUDE.md / SL-25):
//   - companyId/serviceTypeId/fieldDefinitionId NEVER in request bodies — from TenantContext.
//   - data_type + key are IMMUTABLE after creation (FieldDefinitionUpdateSchema uses .strict()).
//   - DataTypeSchema is the closed 8-value vocabulary — never extend from config.
// ---------------------------------------------------------------------------

/**
 * Request body for creating a new field definition (admin only).
 * companyId is NOT here — it comes from TenantContext (KC-3.5 / SL-25).
 *
 * Spec: SL-25-A / design §Backend-Contracts
 */
export const FieldDefinitionCreateSchema = z
  .object({
    serviceTypeId: z.string().uuid('serviceTypeId must be a valid UUID'),
    key: z.string().min(1, 'key is required').max(128, 'key must be at most 128 characters'),
    label: z.string().min(1, 'label is required').max(256, 'label must be at most 256 characters'),
    /** Closed vocabulary — red line #1 from LOCX CLAUDE.md. */
    dataType: DataTypeSchema,
    required: z.boolean().optional().default(false),
    defaultValue: z.string().optional(),
    position: z.number().int().optional().default(0),
    isGridColumn: z.boolean().optional().default(false),
    gridPosition: z.number().int().optional(),
    minValue: z.string().optional(),
    maxValue: z.string().optional(),
    maxLength: z.number().int().optional(),
    pattern: z.string().optional(),
  })
  .strict(); // Rejects companyId/unknown fields — companyId always comes from TenantContext (KC-3.5)

export type FieldDefinitionCreate = z.infer<typeof FieldDefinitionCreateSchema>;

/**
 * Request body for updating an existing field definition (admin only).
 *
 * KEY INVARIANT: data_type and key are IMMUTABLE after creation.
 * .strict() rejects any payload that includes data_type or key (SL-25-B).
 * The SQL SET clause in the repository also never touches these columns.
 *
 * Spec: SL-25-B / design §Backend-Contracts
 */
export const FieldDefinitionUpdateSchema = z
  .object({
    label: z.string().min(1).max(256).optional(),
    required: z.boolean().optional(),
    defaultValue: z.string().nullable().optional(),
    position: z.number().int().optional(),
    isGridColumn: z.boolean().optional(),
    gridPosition: z.number().int().nullable().optional(),
    minValue: z.string().nullable().optional(),
    maxValue: z.string().nullable().optional(),
    maxLength: z.number().int().nullable().optional(),
    pattern: z.string().nullable().optional(),
    active: z.boolean().optional(),
  })
  .strict(); // Rejects dataType/key/companyId/serviceTypeId if supplied — load-bearing.

export type FieldDefinitionUpdate = z.infer<typeof FieldDefinitionUpdateSchema>;

/**
 * One field option row as returned in admin responses.
 * Includes the DB id (needed for PATCH/DELETE routes).
 *
 * Spec: SL-25-C / design §Backend-Contracts
 */
export const FieldOptionAdminSchema = z.object({
  id: z.string().uuid(),
  fieldDefinitionId: z.string().uuid(),
  value: z.string(),
  label: z.string(),
  position: z.number().int(),
});

export type FieldOptionAdmin = z.infer<typeof FieldOptionAdminSchema>;

/**
 * Raw admin response for a single field definition.
 * NEVER calls FieldResolver — returns the DB row as-is, including inactive rows.
 * `editable` = true when companyId is set (tenant-owned); false for base system fields.
 *
 * Spec: SL-25-C / design §Backend-Contracts
 */
export const FieldDefinitionAdminResponseSchema = z.object({
  id: z.string().uuid(),
  /** null = base system field (not editable by tenant); set = tenant-owned field. */
  companyId: z.string().uuid().nullable(),
  serviceTypeId: z.string().uuid(),
  key: z.string(),
  label: z.string(),
  dataType: DataTypeSchema,
  required: z.boolean(),
  defaultValue: z.string().nullable(),
  position: z.number().int(),
  isGridColumn: z.boolean(),
  gridPosition: z.number().int().nullable(),
  minValue: z.string().nullable(),
  maxValue: z.string().nullable(),
  maxLength: z.number().int().nullable(),
  pattern: z.string().nullable(),
  active: z.boolean(),
  /** True when companyId is set (this tenant can edit/deactivate). False for base fields. */
  editable: z.boolean(),
  /** Options for data_type='select'; empty array for all other types. */
  options: z.array(FieldOptionAdminSchema),
});

export type FieldDefinitionAdminResponse = z.infer<typeof FieldDefinitionAdminResponseSchema>;

/**
 * List response for GET /admin/locx/fields?serviceTypeId=...
 *
 * Spec: SL-25-C / design §Backend-Contracts
 */
export const FieldDefinitionAdminListResponseSchema = z.object({
  items: z.array(FieldDefinitionAdminResponseSchema),
});

export type FieldDefinitionAdminListResponse = z.infer<typeof FieldDefinitionAdminListResponseSchema>;

/**
 * Request body for creating a field option (admin only).
 * fieldDefinitionId comes from the route parameter, never the body.
 *
 * Spec: SL-25-C / design §Backend-Contracts
 */
export const FieldOptionCreateSchema = z
  .object({
    value: z.string().min(1, 'value is required').max(256, 'value must be at most 256 characters'),
    label: z.string().min(1, 'label is required').max(256, 'label must be at most 256 characters'),
    position: z.number().int().optional().default(0),
  })
  .strict(); // fieldDefinitionId must not appear in body

export type FieldOptionCreate = z.infer<typeof FieldOptionCreateSchema>;

/**
 * Request body for updating a field option (admin only).
 * `value` is intentionally excluded — changing a live option value would corrupt
 * historical EAV data (service_order_field_value rows). Only label + position can change.
 *
 * Spec: SL-25-C / design §Backend-Contracts
 */
export const FieldOptionUpdateSchema = z
  .object({
    label: z.string().min(1).max(256).optional(),
    position: z.number().int().optional(),
  })
  .strict(); // value/fieldDefinitionId/id must not appear

export type FieldOptionUpdate = z.infer<typeof FieldOptionUpdateSchema>;

// ---------------------------------------------------------------------------
// Asset attribute def admin contracts — add-locx-assets-feature PR2 (AAD-1)
//
// IMPORTANT rules (AAD-1 spec / LOCX CLAUDE.md):
//   - asset_type_id / company_id NEVER in request bodies — assetTypeId from route param,
//     company_id from TenantContext (KC-3.5).
//   - data_type + key are IMMUTABLE after creation — AssetAttributeDefUpdateSchema uses .strict().
//   - NO is_grid_column / grid_position / default_value — asset attribute defs are simpler
//     than field_definition (AAD-1.4 / Proposal decision 6, permanent absence).
//   - DataTypeSchema is the closed 8-value vocabulary.
// ---------------------------------------------------------------------------

/**
 * Response shape for a single asset attribute option (used within admin responses).
 *
 * Spec: AAD-1.1 / design §PR2 backend
 */
export const AssetAttributeOptionResponseSchema = z.object({
  id: z.string().uuid(),
  attribute_def_id: z.string().uuid(),
  value: z.string(),
  label: z.string(),
  position: z.number().int(),
});

export type AssetAttributeOptionResponse = z.infer<typeof AssetAttributeOptionResponseSchema>;

/**
 * Request body for creating a new asset attribute def (admin only).
 * asset_type_id comes from the route parameter (:assetTypeId), NEVER from the body.
 * company_id comes from TenantContext (KC-3.5).
 * active defaults to true on creation.
 *
 * PERMANENT ABSENCE: is_grid_column, grid_position, default_value are excluded.
 * These are field_definition-only concerns; asset_attribute_def is simpler (AAD-1.4).
 *
 * Spec: AAD-1.1 / design §PR2 backend
 */
export const AssetAttributeDefCreateSchema = z
  .object({
    key: z.string().min(1, 'key is required').max(128, 'key must be at most 128 characters'),
    label: z.string().min(1, 'label is required').max(256, 'label must be at most 256 characters'),
    /** Closed vocabulary — red line #1 from LOCX CLAUDE.md. */
    data_type: DataTypeSchema,
    required: z.boolean().optional().default(false),
    position: z.number().int().min(0).optional().default(0),
    min_value: z.number().optional(),
    max_value: z.number().optional(),
    max_length: z.number().int().positive().optional(),
    pattern: z.string().optional(),
  })
  .strict(); // Rejects asset_type_id/company_id/active/is_grid_column/grid_position/default_value.

export type AssetAttributeDefCreate = z.infer<typeof AssetAttributeDefCreateSchema>;

/**
 * Request body for updating an existing asset attribute def (admin only).
 *
 * KEY INVARIANT: data_type and key are IMMUTABLE after creation.
 * .strict() rejects any payload that includes data_type or key (AAD-1.1 / AAD-1-B).
 * asset_type_id is also excluded (comes from route param).
 *
 * Spec: AAD-1.1 / design §PR2 backend
 */
export const AssetAttributeDefUpdateSchema = z
  .object({
    label: z.string().min(1).max(256).optional(),
    required: z.boolean().optional(),
    position: z.number().int().optional(),
    min_value: z.number().nullable().optional(),
    max_value: z.number().nullable().optional(),
    max_length: z.number().int().nullable().optional(),
    pattern: z.string().nullable().optional(),
  })
  .strict(); // Rejects key/data_type/asset_type_id — load-bearing immutability enforcement.

export type AssetAttributeDefUpdate = z.infer<typeof AssetAttributeDefUpdateSchema>;

/**
 * Admin response shape for a single asset attribute def.
 * Includes nested options array (populated for select-type defs).
 * `editable` is NOT included (asset defs have no system-wide companyId, only
 * tenant-owned types expose attribute defs for mutation via the ownership gate).
 *
 * Spec: AAD-1.1 / design §PR2 backend
 */
export const AssetAttributeDefAdminResponseSchema = z.object({
  id: z.string().uuid(),
  asset_type_id: z.string().uuid(),
  key: z.string(),
  label: z.string(),
  data_type: DataTypeSchema,
  required: z.boolean(),
  position: z.number().int(),
  min_value: z.number().nullable(),
  max_value: z.number().nullable(),
  max_length: z.number().int().nullable(),
  pattern: z.string().nullable(),
  active: z.boolean(),
  options: z.array(AssetAttributeOptionResponseSchema),
});

export type AssetAttributeDefAdminResponse = z.infer<typeof AssetAttributeDefAdminResponseSchema>;

/**
 * Request body for creating an asset attribute option (admin only).
 * attribute_def_id comes from the route parameter, NEVER from the body.
 *
 * Spec: AAD-1.1 / design §PR2 backend
 */
export const AssetAttributeOptionCreateSchema = z
  .object({
    value: z.string().min(1, 'value is required').max(256, 'value must be at most 256 characters'),
    label: z.string().min(1, 'label is required').max(256, 'label must be at most 256 characters'),
    position: z.number().int().min(0).optional().default(0),
  })
  .strict(); // attribute_def_id must not appear in body

export type AssetAttributeOptionCreate = z.infer<typeof AssetAttributeOptionCreateSchema>;

/**
 * Request body for updating an asset attribute option (admin only).
 * `value` is intentionally excluded — changing a live option value would corrupt
 * historical EAV data (asset_attribute_value rows). Only label + position can change.
 *
 * Spec: AAD-1.1 / design §PR2 backend
 */
export const AssetAttributeOptionUpdateSchema = z
  .object({
    label: z.string().min(1).max(256).optional(),
    position: z.number().int().optional(),
  })
  .strict(); // value/attribute_def_id/id must not appear

export type AssetAttributeOptionUpdate = z.infer<typeof AssetAttributeOptionUpdateSchema>;
